import { useMemo, useEffect } from "react";
import { Field } from "react-final-form";
import { format } from "date-fns";

import InputText from "@components/Form/InputText";
import Select from "@components/Form/Select";
import {
  composeFormValidators,
  hasMaxLength,
  hasMaxVal,
  hasMinLength,
  hasMinVal,
  isRequired,
  isVehiclePlateNumber,
  isAlphaNumeric,
} from "@lib/validations";
import SelectSearch from "@components/Form/SelectSearch";
import CustomDatePicker from "@components/CustomDatePicker";

const INPUT_CLASS = "!max-w-full !min-w-0";

const VehicleDataSection = ({
  apiData,
  form,
  getFormData,
  onFormDataUpdate,
  formData,
}) => {
  const {
    vehicleCategories,
    vehicleSubcategories,
    vehicleBrands,
    vehicleFuelTypes,
    vehicleActivityTypes,
  } = apiData;

  const { values } = form.getState();

  const isElectricMotor = useMemo(() => {
    if (!values.vehicle?.fuelTypeId || !vehicleFuelTypes) {
      return false;
    }
    const selectedFuelType = vehicleFuelTypes.find(
      (type) => type.id === Number(values.vehicle.fuelTypeId)
    );
    return selectedFuelType?.name?.toLowerCase().includes("electric");
  }, [values.vehicle?.fuelTypeId, vehicleFuelTypes]);

  useEffect(() => {
    if (isElectricMotor) {
      form.change("vehicle.engineCapacity", "1");
    }
  }, [isElectricMotor, form]);

  const vehicleCategoriesOptions = useMemo(
    () =>
      vehicleCategories
        ? vehicleCategories.map(({ id, name }) => ({ value: id, label: name }))
        : [],
    [vehicleCategories]
  );

  const vehicleSubcategoriesOptions = useMemo(
    () =>
      vehicleSubcategories &&
      values.vehicle.vehicleCategoryId &&
      vehicleSubcategories[values.vehicle.vehicleCategoryId]
        ? vehicleSubcategories[values.vehicle.vehicleCategoryId].map(
            ({ id, name }) => ({
              value: id,
              label: name,
            })
          )
        : [],
    [vehicleSubcategories, values.vehicle.vehicleCategoryId]
  );

  const vehicleBrandsOptions = useMemo(
    () =>
      vehicleBrands
        ? vehicleBrands.map(({ id, name }) => ({ value: id, label: name }))
        : [],
    [vehicleBrands]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      <div>
        <Field
          name="vehicle.vehicleCategoryId"
          validate={composeFormValidators(isRequired())}
          component={Select}
          label="Categorie vehicul"
          required
          labelClass="text-[14px]"
          inputClassName={INPUT_CLASS}
        >
          <option value="">alege</option>

          {vehicleCategoriesOptions &&
            vehicleCategoriesOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
        </Field>
      </div>

      <div>
        <Field
          name="vehicle.vehicleSubCategoryId"
          label="Subcategorie vehicul"
          component={Select}
          required
          disabled={!values.vehicle.vehicleCategoryId}
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired())}
          inputClassName={INPUT_CLASS}
        >
          <option value="">alege</option>

          {vehicleSubcategoriesOptions &&
            vehicleSubcategoriesOptions.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
        </Field>
      </div>

      {values.vehicle?.vehicleRegistrationStatus === "1" && (
        <div>
          <Field
            name="vehicle.plateNo"
            component={InputText}
            label="Nr. de inmatriculare"
            required
            placeholder="B 000 ABC"
            labelClass="text-[14px]"
            inputClassName={INPUT_CLASS}
            validate={composeFormValidators(
              isRequired(),
              isVehiclePlateNumber()
            )}
          />
        </div>
      )}

      <div>
        <Field
          name="vehicle.vin"
          validate={composeFormValidators(isRequired())}
        >
          {({ input, meta }) => (
            <InputText
              input={{
                ...input,
                value: input.value,
                onChange: (e) => {
                  const value = e.target.value;
                  if (value?.length > 17) {
                    return;
                  }

                  input.onChange(value);
                  if (value.trim().length === 17) {
                    getFormData({
                      type: "vinCheckData",
                      payload: { vin: value, form },
                    });
                  }
                },
              }}
              meta={meta}
              label="Serie sasiu"
              required
              max="17"
              inputClassName={INPUT_CLASS}
              disabled={values.vin?.length > 17}
              placeholder="Introdu seria de sasiu"
              labelClass="text-[14px]"
            />
          )}
        </Field>
      </div>

      <div>
        <Field
          name="vehicle.makeId"
          label="Marca"
          required
          labelClass="text-[14px]"
          component={SelectSearch}
          placeholder="alege"
          validate={composeFormValidators(isRequired())}
          parse={(option) => option.value}
          format={(value) =>
            vehicleBrandsOptions.find((option) => option.value === value)
          }
          options={vehicleBrandsOptions}
          controlClassName={INPUT_CLASS}
        />
      </div>

      <div>
        <Field
          name="vehicle.commercialName"
          component={InputText}
          label="Model"
          required
          placeholder="Introdu modelul"
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired())}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div>
        <Field
          name="vehicle.productionYear"
          component={InputText}
          type="number"
          label="An fabricatie"
          required
          placeholder={new Date().getFullYear().toString()}
          min="1900"
          max={new Date().getFullYear() + 1}
          labelClass="text-[14px]"
          validate={composeFormValidators(
            isRequired(),
            hasMinLength(4),
            hasMaxLength(4),
            hasMinVal(1900),
            hasMaxVal(new Date().getFullYear())
          )}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div>
        <Field
          name="vehicle.fuelTypeId"
          label="Tip motor"
          required
          labelClass="text-[14px]"
          component={Select}
          validate={composeFormValidators(isRequired())}
          inputClassName={INPUT_CLASS}
        >
          <option value="">alege</option>

          {vehicleFuelTypes &&
            vehicleFuelTypes.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
        </Field>
      </div>

      <div>
        <Field
          name="vehicle.engineCapacity"
          component={InputText}
          label="Capacitate cilindrica"
          type="number"
          required
          placeholder="1234"
          min="1"
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired(), hasMinVal(1))}
          inputClassName={INPUT_CLASS}
          disabled={isElectricMotor}
        />
      </div>

      <div>
        <Field
          name="vehicle.maxWeight"
          component={InputText}
          label="Masa maxima"
          type="number"
          required
          placeholder="1234"
          min="1"
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired(), hasMinVal(1))}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div>
        <Field
          name="vehicle.enginePowerKw"
          component={InputText}
          label="Putere (kW)"
          type="number"
          required
          placeholder="123"
          min="1"
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired(), hasMinVal(1))}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div>
        <Field
          name="vehicle.seatsNumber"
          component={InputText}
          label="Nr. locuri"
          type="number"
          required
          placeholder="5"
          min="1"
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired(), hasMinVal(1))}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div className="col-span-1 md:col-span-2">
        <Field
          name="vehicle.civ"
          component={InputText}
          label="Serie CIV"
          required
          placeholder="Introdu seria cartii de identitate a vehiculului"
          labelClass="text-[14px]"
          validate={composeFormValidators(
            isRequired(),
            isAlphaNumeric(),
            hasMinLength(7),
            hasMaxLength(7)
          )}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div className="w-full ">
        <span className="text-[14px] Form-label is-required font-medium mb-[4px]">
          Data expirare ITP
        </span>

        <CustomDatePicker
          selected={
            formData.vehicle.itpExpiration
              ? new Date(formData.vehicle.itpExpiration)
              : null
          }
          onChange={(date) => {
            onFormDataUpdate({
              vehicle: {
                ...formData.vehicle,
                itpExpiration: format(date, "yyyy-MM-dd'T'00:00:00"),
              },
            });
          }}
        />
      </div>

      <div>
        <Field
          name="vehicle.graweKilometraj"
          component={InputText}
          type="number"
          label="Numar de km"
          required
          placeholder="Introdu numarul de km"
          labelClass="text-[14px]"
          validate={composeFormValidators(isRequired(), hasMinVal(0))}
          inputClassName={INPUT_CLASS}
        />
      </div>

      <div>
        <Field
          name="vehicle.activityTypeId"
          validate={composeFormValidators(isRequired())}
          required
          label="Tip utilizare vehicul"
          component={Select}
          labelClass="text-[14px]"
          inputClassName={INPUT_CLASS}
        >
          <option value="">alege</option>

          {vehicleActivityTypes &&
            vehicleActivityTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
        </Field>
      </div>
    </div>
  );
};

export default VehicleDataSection;
